import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";

/* Progressive cursor-list primitive for compact shell surfaces (PB-42).

   The repo's convention is keyset ("cursor") pagination everywhere — every
   list endpoint takes `limit` + an opaque `cursor` and hands back the next one
   (see services/playbook-be/src/common/pagination/ and
   services/auth/src/admin/admin.controller.ts). Eight call sites had already
   hand-rolled the same `useInfiniteQuery` shape; this collects it in one place
   so a new admin list is a registry-style declaration rather than a copy.
   Modelled on the clearest of those, packages/kpi-feature's `useKpis`.

   Deliberately NOT a component and NOT in packages/ui: it fetches and
   accumulates data, while packages/ui is data-blind by invariant. Full-page
   cursor views use the UI package's page-step state instead. */

/** One page as this hook needs it. Callers adapt their endpoint's envelope —
    auth's `{users, nextCursor}` and playbook-be's `{data, nextCursor, hasMore}`
    both normalize to this. A null cursor means "no more pages". */
export interface CursorPage<T> {
  readonly items: readonly T[];
  readonly nextCursor: string | null;
  /** The count of rows matching the FILTER, when the endpoint reports one.
      Most cursor endpoints deliberately do not — counting is what keyset
      pagination exists to avoid — so this is optional and a caller that reads
      it must handle `undefined`. Where a server does compute it (auth's team
      search windows a `count(*) OVER ()` into the same query it is already
      running), dropping it on the floor would force a list to describe itself
      as "12 shown" when the workspace has 400. */
  readonly totalCount?: number;
}

export interface UseProgressiveCursorListOptions<T> {
  /** Include every server-side filter here — a filter change must start a new
      page set, not append to the previous one. */
  readonly queryKey: readonly unknown[];
  readonly fetchPage: (cursor: string | null) => Promise<CursorPage<T>>;
  readonly enabled?: boolean;
  readonly staleTime?: number;
  /**
   * Keep the previous key's rows on screen while a new key loads. For a list
   * whose key changes as somebody TYPES (a search box): without it every
   * keystroke empties the list and refills it, so the table flashes away and
   * back on each letter. The rows shown meanwhile belong to the previous
   * search and are replaced as one set when the new one lands — never merged
   * with it, because the key still starts a new page set.
   */
  readonly keepPreviousWhileLoading?: boolean;
}

export interface ProgressiveCursorListResult<T> {
  /** Every page fetched so far, flattened in server order. */
  readonly items: readonly T[];
  readonly hasMore: boolean;
  /** From the FIRST page, because that is the one whose filter the reader is
      looking at and every page of a set reports the same figure. `undefined`
      when the endpoint does not report one. */
  readonly totalCount: number | undefined;
  /** First page in flight — render a skeleton, not an empty state. */
  readonly isLoading: boolean;
  readonly isLoadingMore: boolean;
  /** The last NEXT-page request failed. The rows already loaded stay, and
      `error` is set too — this says which kind of failure it was, so a list
      can report it beside the rows rather than replacing them. */
  readonly loadMoreFailed: boolean;
  /** Settled with genuinely zero rows: safe to show an empty state. Distinct
      from `isLoading`, so a slow first page never flashes "nothing here". */
  readonly isEmpty: boolean;
  readonly error: Error | null;
  readonly loadMore: () => void;
  /* Without this a list built on this hook cannot offer a way back from a
     failure, which is half of reporting one. */
  readonly refetch: () => void;
}

export function useProgressiveCursorList<T>({
  queryKey,
  fetchPage,
  enabled = true,
  staleTime,
  keepPreviousWhileLoading = false,
}: UseProgressiveCursorListOptions<T>): ProgressiveCursorListResult<T> {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      fetchPage(pageParam),
    initialPageParam: null as string | null,
    /* Returning undefined is what tells TanStack the set is complete, so a
       null cursor must be mapped rather than passed through. */
    getNextPageParam: (last: CursorPage<T>) => last.nextCursor ?? undefined,
    enabled,
    ...(staleTime === undefined ? {} : { staleTime }),
    ...(keepPreviousWhileLoading ? { placeholderData: keepPreviousData } : {}),
  });

  const items = query.data?.pages.flatMap((page) => [...page.items]) ?? [];

  return {
    items,
    hasMore: query.hasNextPage,
    totalCount: query.data?.pages[0]?.totalCount,
    /* `isPending` alone would be permanently true for a DISABLED query — it
       means "no data yet", and a query that never runs never gets data. Pairing
       it with `isFetching` is how TanStack itself defines `isLoading`, and it
       keeps a gated list (enabled: false) from rendering a spinner forever. */
    isLoading:
      (query.isPending && query.isFetching) ||
      /* Carried-over rows are the point of `keepPreviousWhileLoading`, but
         carried-over EMPTINESS is a claim about a search the server has not
         answered: an empty previous result must read as loading, not as "no
         match" for the new key. */
      (query.isPlaceholderData && items.length === 0),
    isLoadingMore: query.isFetchingNextPage,
    loadMoreFailed: query.isFetchNextPageError,
    isEmpty: query.isSuccess && !query.isPlaceholderData && items.length === 0,
    error: query.error,
    /* Guarded so a double-click (or a click while the tail is in flight)
       can't queue duplicate page requests. */
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) {
        void query.fetchNextPage();
      }
    },
    refetch: () => {
      /* `cancelRefetch: false` rather than a guard on `query.isFetching`.
         That flag is a RENDER snapshot, so two clicks in the same frame both
         pass it; the option is evaluated by the query itself, so a second
         Retry joins the refetch already running instead of cancelling it and
         starting every cached page again. Deliberately a different shape from
         `loadMore` above, which has no equivalent option. */
      void query.refetch({ cancelRefetch: false });
    },
  };
}
